import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import {
  Button,
  Card,
  Flex,
  Form,
  InputNumber,
  Select,
  Skeleton,
  Switch,
  Table,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import BackPageHeading from "components/BackPageHeading.component";
import { ReadOnlyWhenRestricted } from "components/capability";
import {
  SettingRow,
  SettingsPage,
  SettingsSection,
  SettingsSections,
  SettingsSectionsFooter,
} from "pages/settings/settingsRows";
import { Link } from "react-router-dom";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { getAllProductsByBusinessId } from "hooks/queries/product";
import { usePendingReferrals, useReferralConfig } from "hooks/queries/referral";
import {
  useDeactivateReferralProgram,
  useUpdateReferralConfig,
} from "hooks/mutations/referral";
import { showConfirmModal } from "components/modals/confirmModal";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import {
  RewardTypeSpecificFields,
  sameFormValues,
  unitAddon,
} from "pages/settings/rewardSettingsShared";
import {
  formatCurrency,
  PROGRAM_STATUS_LABELS,
  PROGRAM_STATUS_TONE,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_TONE,
} from "shared/utils";
import { translate } from "shared/languages";
import { DEFAULT_DATE_FORMAT, formatDate } from "shared/utils/dayjs";
import { rewardLabel } from "shared/utils/loyalty";
import {
  ExpiryUnit,
  LoyaltyClassScope,
  LoyaltyDiscountScope,
  LoyaltyRewardType,
  ReferralCapWindow,
  ReferralProgramStatus,
  ReferralStatus,
} from "shared/types/enum";
import {
  PendingReferralRow,
  ReferralConfig,
  ReferralRewardConfig,
  ReferralRewardForm,
  UpdateReferralConfigForm,
} from "shared/types/referral";
import { ProductType } from "shared/types/enum";
import { colors } from "shared/theme/color";
import PATHS from "shared/constants/paths";
import { textPx } from "shared/constants/zoom";

const { Title, Text } = Typography;

// One side's reward as held by the AntD form (before payload mapping).
type RewardFormRow = {
  reward_type?: string;
  valid_value?: number;
  valid_unit?: string;
  credit_qty?: number;
  class_scope?: string;
  class_type_ids?: string[];
  discount_mode?: string;
  discount_value?: number;
  discount_scope?: string;
  product_ids?: string[];
};

// An empty reward builder: no type chosen, so no type-specific fields render
// until the studio picks one. The unit rides along because it is the addon on
// a blank validity input, not a value of its own.
const emptyReward: RewardFormRow = {
  valid_unit: ExpiryUnit.Day,
};

const rewardToFormRow = (r: ReferralRewardConfig): RewardFormRow => ({
  reward_type: r.reward_type,
  valid_value: r.valid_value,
  valid_unit: r.valid_unit ?? ExpiryUnit.Day,
  credit_qty: r.credit_qty ?? undefined,
  class_scope: r.class_scope ?? undefined,
  class_type_ids: r.class_type_ids,
  discount_mode: r.discount_mode ?? undefined,
  discount_value: r.discount_value ?? undefined,
  discount_scope: r.discount_scope ?? undefined,
  product_ids: r.product_ids,
});

const rewardRowToPayload = (m: RewardFormRow): ReferralRewardForm => ({
  reward_type: m.reward_type!,
  valid_value: Number(m.valid_value),
  valid_unit: m.valid_unit!,
  ...(m.reward_type === LoyaltyRewardType.FreeCredit
    ? {
        credit_qty: Number(m.credit_qty),
        class_scope: m.class_scope,
        ...(m.class_scope === LoyaltyClassScope.Selected
          ? { class_type_ids: m.class_type_ids ?? [] }
          : {}),
      }
    : {
        discount_mode: m.discount_mode,
        discount_value: Number(m.discount_value),
        discount_scope: m.discount_scope,
        ...(m.discount_scope === LoyaltyDiscountScope.Products
          ? { product_ids: m.product_ids ?? [] }
          : {}),
      }),
});

// A fresh program's form values — used while the program is off (default).
//
// Deliberately BLANK (Andrew, 2026-08-20): the pre-filled 90-day attribution
// and the two drafted rewards read as a configured program the studio never
// chose. "Reward teman" is the one thing that starts on — every other toggle
// starts off, and both reward builders start empty (reward_type and
// valid_value are required, so a half-filled side can't be saved). Unit
// pickers keep their unit; the paired VALUE is what's blank.
const emptyFormValues = () => ({
  has_min_purchase: false,
  min_purchase_amount: undefined,
  has_attribution_validity: false,
  attribution_validity_value: undefined,
  attribution_validity_unit: ExpiryUnit.Day,
  has_referral_cap: false,
  referral_cap: undefined,
  referral_cap_window: ReferralCapWindow.Month,
  referrer_reward: { ...emptyReward },
  has_friend_reward: true,
  friend_reward: { ...emptyReward },
});

// Pengaturan -> Sistem Reward -> Referral Program (any Admin since 2026-08-20;
// no role gate on the route). One full-document form split over two cards — program
// settings and the two reward builders — plus the pending-referrals list.
// Unlike loyalty, config is read LIVE at conversion, so saves apply to
// pending referrals too; the confirm copy says so.
const ReferralSettingsCard = () => {
  // Pembatasan Admin: the page's ReadOnlyWhenRestricted disables every
  // control through DisabledContext EXCEPT one with an explicit `disabled`
  // prop; the save button has one, so it reads the restriction itself.
  const { isRestricted } = useAdminCapabilities();
  const settingsRestricted = isRestricted(AdminCapability.PromoReward);
  const { data: config, isLoading } = useReferralConfig();
  const { data: pending } = usePendingReferrals();
  const { data: classes = [] } = getAllClassTypeByBusinessId();
  const { data: productList } = getAllProductsByBusinessId();
  const updateConfig = useUpdateReferralConfig();
  const deactivateProgram = useDeactivateReferralProgram();
  const [form] = useForm();
  // "Dirty" is derived against a snapshot of the last-known-saved values
  // (set at hydration, and re-set after a successful save/deactivate), not a
  // manually-toggled boolean — a boolean only ever flips true on edit and
  // never derives back to false on its own (e.g. if a field gets reverted
  // programmatically), the same staleness PolicySettingsPage's dirty flag had
  // before being fixed to this same pattern.
  const [baseline, setBaseline] = useState<Record<string, unknown> | null>(null);
  // Off (the default) hides the form behind an explicit "turn on" step;
  // the program goes live on the first save (loyalty's pattern).
  const [configuring, setConfiguring] = useState(false);

  const status = config?.program?.status ?? ReferralProgramStatus.Off;

  const hasMinPurchase = Form.useWatch("has_min_purchase", form);
  const hasValidity = Form.useWatch("has_attribution_validity", form);
  const hasCap = Form.useWatch("has_referral_cap", form);
  const hasFriendReward = Form.useWatch("has_friend_reward", form);

  // Hydrate once (the hydratedRef pattern). An OFF program is a finished
  // campaign, so it hydrates as a FRESH form (loyalty's pattern) — turning the
  // program back on must not resurrect the previous campaign's settings.
  //
  // The STORED config is untouched by this: it stays on the row while the
  // program is off, which is what pending referrals keep paying out from, and
  // only a save overwrites it.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (isLoading || hydratedRef.current) return;
    hydratedRef.current = true;
    const raw: ReferralConfig | null = config?.program ?? null;
    const program =
      raw && raw.status !== ReferralProgramStatus.Off ? raw : null;
    const hydrated = program
      ? {
          has_min_purchase: program.min_purchase_amount != null,
          min_purchase_amount: program.min_purchase_amount ?? undefined,
          has_attribution_validity:
            program.attribution_validity_value != null,
          attribution_validity_value:
            program.attribution_validity_value ?? undefined,
          attribution_validity_unit:
            program.attribution_validity_unit ?? ExpiryUnit.Day,
          has_referral_cap: program.referral_cap != null,
          referral_cap: program.referral_cap ?? undefined,
          referral_cap_window:
            program.referral_cap_window ?? ReferralCapWindow.Month,
          referrer_reward: program.referrer_reward
            ? rewardToFormRow(program.referrer_reward)
            : { ...emptyReward },
          has_friend_reward: program.friend_reward != null,
          friend_reward: program.friend_reward
            ? rewardToFormRow(program.friend_reward)
            : { ...emptyReward },
        }
      : emptyFormValues();
    form.setFieldsValue(hydrated);
    setBaseline(form.getFieldsValue(true));
  }, [config, isLoading, form]);

  const watchedValues = Form.useWatch([], { form, preserve: true });
  const dirty =
    !!baseline && !!watchedValues && !sameFormValues(watchedValues, baseline);

  const products = useMemo(() => productList?.products ?? [], [productList]);
  // Whole catalog products (never tiers), Paket group first then Membership —
  // the same grouped options the loyalty builder uses.
  const productOptions = useMemo(() => {
    const toOption = (p: { id: string; name: string }) => ({
      value: p.id,
      label: p.name,
    });
    const credit = products
      .filter((p) => p.product_type === ProductType.Credit)
      .map(toOption);
    const memberships = products
      .filter((p) => p.product_type === ProductType.Membership)
      .map(toOption);
    const groups = [];
    if (credit.length > 0) {
      groups.push({
        label: translate("package"),
        title: translate("package"),
        options: credit,
      });
    }
    if (memberships.length > 0) {
      groups.push({
        label: translate("membership"),
        title: translate("membership"),
        options: memberships,
      });
    }
    return groups;
  }, [products]);

  const heading = (extra?: ReactNode) => (
    <BackPageHeading
      title={translate("referral.card_title")}
      backTo={PATHS.SETTINGS}
      extra={extra}
    />
  );

  if (isLoading) {
    return (
      <SettingsPage>
        {heading()}
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </SettingsPage>
    );
  }

  const pendingRows = pending?.referrals ?? [];
  const pendingColumns = [
    {
      title: translate("referral.pending.col_client"),
      dataIndex: "referred_name",
      key: "referred_name",
      render: (name: string, row: PendingReferralRow) => (
        <Link
          to={`${PATHS.CUSTOMER}/${row.referred_customer_id}?tab=5&reward=referral`}
        >
          {name}
        </Link>
      ),
    },
    {
      title: translate("referral.pending.col_referrer"),
      dataIndex: "referrer_name",
      key: "referrer_name",
    },
    {
      title: translate("referral.pending.col_registered"),
      dataIndex: "created_at",
      key: "created_at",
      render: (d: string) => formatDate(d, DEFAULT_DATE_FORMAT),
    },
    {
      title: translate("referral.pending.col_expires"),
      dataIndex: "expires_at",
      key: "expires_at",
      render: (d: string | null) =>
        d ? formatDate(d, DEFAULT_DATE_FORMAT) : "-",
    },
    {
      // What this referral will pay: frozen at registration, so a later
      // config save never changes it (R25).
      title: translate("referral.pending.col_rewards"),
      key: "rewards",
      render: (_: unknown, row: PendingReferralRow) => (
        <Flex vertical>
          <Text style={{ fontSize: textPx(13) }}>
            {translate("referral.pending.rewards_referrer")}:{" "}
            {row.referrer_reward ? rewardLabel(row.referrer_reward) : "-"}
          </Text>
          <Text style={{ fontSize: textPx(13) }}>
            {translate("referral.pending.rewards_friend")}:{" "}
            {row.friend_reward ? rewardLabel(row.friend_reward) : "-"}
          </Text>
        </Flex>
      ),
    },
    {
      title: translate("referral.pending.col_status"),
      dataIndex: "status",
      key: "status",
      render: (s: string) =>
        s === ReferralStatus.Pending ? (
          <StatusBadge
            tone={REFERRAL_STATUS_TONE[ReferralStatus.Pending]}
            label={REFERRAL_STATUS_LABELS[ReferralStatus.Pending]}
          />
        ) : (
          <StatusBadge tone={REFERRAL_STATUS_TONE.lapsed} label={REFERRAL_STATUS_LABELS.lapsed} />
        ),
    },
  ];

  // The pending list renders in BOTH states: while the programme is off it is
  // what the studio still owes, with each row's promised rewards (Andrew,
  // 2026-09-29: the real table, not a summary). It is not a settings group,
  // so it spans the page width with its name above instead of beside it
  // (Andrew, 2026-10-07).
  const pendingCard = (
    <SettingsSection
      span
      flush
      title={translate("referral.pending.title")}
      description={translate("referral.pending.hint")}
    >
      <Table
        rowKey="id"
        columns={pendingColumns}
        dataSource={pendingRows}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        size="middle"
      />
    </SettingsSection>
  );

  // Off by default: businesses opt in. The form only appears after the
  // explicit "turn on" step; the program itself goes live on the first save.
  if (status === ReferralProgramStatus.Off && !configuring) {
    return (
      <SettingsPage>
        {heading()}
        <ReadOnlyWhenRestricted capability={AdminCapability.PromoReward}>
          <Card>
            <Flex
              vertical
              align="center"
              gap={6}
              style={{ padding: "36px 16px", textAlign: "center" }}
            >
              <Title level={5} style={{ margin: 0 }}>
                {translate("referral.off.title")}
              </Title>
              <Text type="secondary" style={{ fontSize: textPx(13), maxWidth: 420 }}>
                {translate("referral.off.body")}
              </Text>
              <Button
                type="primary"
                style={{ marginTop: 14 }}
                onClick={() => setConfiguring(true)}
              >
                {translate("referral.off.cta")}
              </Button>
            </Flex>
          </Card>
          <div style={{ marginTop: 28 }}>
            <SettingsSections>{pendingCard}</SettingsSections>
          </div>
        </ReadOnlyWhenRestricted>
      </SettingsPage>
    );
  }

  const classOptions = classes.map((c) => ({ value: c.id, label: c.name }));

  // Cap window dropdown inside the cap InputNumber's addon (unitAddon's
  // sibling). 'forever' turns the cap into an all-time total.
  const capWindowAddon = (
    <Form.Item name="referral_cap_window" noStyle>
      <Select
        style={{ width: 126 }}
        options={[
          {
            value: ReferralCapWindow.Day,
            label: translate("referral.field.cap_window.day"),
          },
          {
            value: ReferralCapWindow.Month,
            label: translate("referral.field.cap_window.month"),
          },
          {
            value: ReferralCapWindow.Year,
            label: translate("referral.field.cap_window.year"),
          },
          {
            value: ReferralCapWindow.Forever,
            label: translate("referral.field.cap_window.forever"),
          },
        ]}
      />
    </Form.Item>
  );

  const rewardCoreFields = (side: "referrer_reward" | "friend_reward") => (
    <div className="reward-fields-grid" style={{ marginTop: 12 }}>
      <Form.Item
        name={[side, "reward_type"]}
        label={translate("loyalty.milestones.reward_type")}
        rules={[{ required: true, message: translate("form.error.required") }]}
      >
        <Select
          options={[
            { value: LoyaltyRewardType.FreeCredit, label: translate("loyalty.reward.free_credit") },
            { value: LoyaltyRewardType.Discount, label: translate("loyalty.reward.discount") },
          ]}
        />
      </Form.Item>
      <Form.Item
        name={[side, "valid_value"]}
        label={translate("loyalty.milestones.valid_days")}
        rules={[
          {
            required: true,
            type: "integer",
            min: 1,
            message: translate("form.error.required"),
          },
        ]}
      >
        <InputNumber
          min={1}
          precision={0}
          style={{ width: "100%" }}
          addonAfter={unitAddon([side, "valid_unit"])}
        />
      </Form.Item>
      {/* keeps the type-specific fields starting on a fresh row */}
      <div aria-hidden />
      <RewardTypeSpecificFields
        namePath={[side]}
        fullPath={[side]}
        classOptions={classOptions}
        productOptions={productOptions}
      />
    </div>
  );

  const onSave = async () => {
    const v = await form.validateFields().catch(() => null);
    if (!v) return;
    const payload: UpdateReferralConfigForm = {
      referrer_reward: rewardRowToPayload(v.referrer_reward as RewardFormRow),
      friend_reward: v.has_friend_reward
        ? rewardRowToPayload(v.friend_reward as RewardFormRow)
        : null,
      min_purchase_amount: v.has_min_purchase
        ? Number(v.min_purchase_amount)
        : null,
      attribution_validity_value: v.has_attribution_validity
        ? Number(v.attribution_validity_value)
        : null,
      attribution_validity_unit: v.has_attribution_validity
        ? v.attribution_validity_unit
        : null,
      referral_cap: v.has_referral_cap ? Number(v.referral_cap) : null,
      referral_cap_window: v.has_referral_cap ? v.referral_cap_window : null,
    };
    showConfirmModal({
      tone: "amber",
      title: translate("referral.confirm.title"),
      content: translate("referral.confirm.body"),
      confirmLabel: translate("button.save_changes"),
      onConfirm: () =>
        updateConfig.mutate(payload, {
          onSuccess: () => {
            // v is exactly what just got saved — the new baseline to diff
            // against, without waiting on a refetch.
            setBaseline(form.getFieldsValue(true));
            setConfiguring(false);
          },
        }),
    });
  };

  const onDeactivate = () => {
    showConfirmModal({
      tone: "error",
      title: translate("referral.lifecycle.deactivate_confirm_title"),
      content: translate("referral.lifecycle.deactivate_confirm_body"),
      confirmLabel: translate("referral.lifecycle.deactivate"),
      onConfirm: () =>
        deactivateProgram.mutate(undefined, {
          onSuccess: () => {
            // Back to the off empty state with a fresh form for the next
            // campaign. The stored config is retained server-side (pending
            // referrals still pay out from it); only the FORM resets, so
            // turning the program back on starts blank.
            setConfiguring(false);
            // resetFields FIRST: setFieldsValue deep-merges plain objects, so
            // the two nested reward builders would keep their old reward_type
            // and amounts rather than going blank.
            form.resetFields();
            const fresh = emptyFormValues();
            form.setFieldsValue(fresh);
            setBaseline(form.getFieldsValue(true));
          },
        }),
    });
  };

  // Program status in the page heading, as on Loyalty Card. The heading is
  // outside the read-only wrapper, so the button reads the restriction itself.
  const headingExtra =
    status === ReferralProgramStatus.Off ? (
      <Text type="secondary" style={{ fontSize: textPx(13) }}>
        {translate("referral.lifecycle.activate_note")}
      </Text>
    ) : (
      <Flex align="center" gap={12}>
        <StatusBadge tone={PROGRAM_STATUS_TONE} label={PROGRAM_STATUS_LABELS.active} />
        <Button
          danger
          size="small"
          disabled={settingsRestricted}
          loading={deactivateProgram.isPending}
          onClick={onDeactivate}
        >
          {translate("referral.lifecycle.deactivate")}
        </Button>
      </Flex>
    );

  const amountInput = (
    <InputNumber
      min={1}
      style={{ width: 200 }}
      aria-label={translate("referral.field.min_purchase")}
      formatter={(value?: number | string) => (value ? formatCurrency(Number(value)) : "")}
      parser={(value?: string) =>
        value ? parseInt(value.replace(/[Rp,.\s]/g, ""), 10) || 0 : 0
      }
    />
  );

  return (
    <SettingsPage>
      {heading(headingExtra)}
      <ReadOnlyWhenRestricted capability={AdminCapability.PromoReward}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <SettingsSections>
            {/* ---- Who qualifies, and for how long ---- */}
            <SettingsSection title={translate("referral.section.rules")}>
              <SettingRow
                title={translate("referral.field.min_purchase")}
                hint={translate("referral.field.min_purchase_hint")}
                control={
                  <Form.Item name="has_min_purchase" valuePropName="checked" noStyle>
                    <Switch aria-label={translate("referral.field.min_purchase")} />
                  </Form.Item>
                }
                below={
                  hasMinPurchase && (
                    <Form.Item
                      name="min_purchase_amount"
                      style={{ margin: "12px 0 0" }}
                      rules={[
                        {
                          required: true,
                          type: "integer",
                          min: 1,
                          message: translate("form.error.required"),
                        },
                      ]}
                    >
                      {amountInput}
                    </Form.Item>
                  )
                }
              />
              <SettingRow
                title={translate("referral.field.validity")}
                hint={translate("referral.field.validity_hint")}
                control={
                  <Form.Item name="has_attribution_validity" valuePropName="checked" noStyle>
                    <Switch aria-label={translate("referral.field.validity")} />
                  </Form.Item>
                }
                below={
                  hasValidity && (
                    <Form.Item
                      name="attribution_validity_value"
                      style={{ margin: "12px 0 0" }}
                      rules={[
                        {
                          required: true,
                          type: "integer",
                          min: 1,
                          message: translate("form.error.required"),
                        },
                      ]}
                    >
                      <InputNumber
                        min={1}
                        precision={0}
                        style={{ width: 200 }}
                        aria-label={translate("referral.field.validity")}
                        addonAfter={unitAddon(["attribution_validity_unit"])}
                      />
                    </Form.Item>
                  )
                }
              />
              <SettingRow
                title={translate("referral.field.cap")}
                hint={translate("referral.field.cap_hint")}
                last
                control={
                  <Form.Item name="has_referral_cap" valuePropName="checked" noStyle>
                    <Switch aria-label={translate("referral.field.cap")} />
                  </Form.Item>
                }
                below={
                  hasCap && (
                    <Form.Item
                      name="referral_cap"
                      style={{ margin: "12px 0 0" }}
                      rules={[
                        {
                          required: true,
                          type: "integer",
                          min: 1,
                          message: translate("form.error.required"),
                        },
                      ]}
                    >
                      <InputNumber
                        min={1}
                        precision={0}
                        style={{ width: 236 }}
                        aria-label={translate("referral.field.cap")}
                        addonAfter={capWindowAddon}
                      />
                    </Form.Item>
                  )
                }
              />
            </SettingsSection>

            {/* ---- What each side receives ---- */}
            <SettingsSection title={translate("referral.section.rewards")}>
              <div style={{ padding: "16px 0 0", borderBottom: `1px solid ${colors.line}` }}>
                <div style={{ fontWeight: 600, fontSize: textPx(14.5) }}>
                  {translate("referral.rewards.referrer")}
                </div>
                <Text type="secondary" className="settings-desc" style={{ fontSize: textPx(13), lineHeight: 1.5, display: "block", marginTop: 2, maxWidth: 520 }}>
                  {translate("referral.rewards.referrer_hint")}
                </Text>
                {rewardCoreFields("referrer_reward")}
              </div>
              <div style={{ padding: "16px 0" }}>
                <Flex justify="space-between" align="center" gap={24}>
                  <div style={{ maxWidth: 520 }}>
                    <div style={{ fontWeight: 600, fontSize: textPx(14.5) }}>
                      {translate("referral.rewards.friend")}
                    </div>
                    <Text type="secondary" className="settings-desc" style={{ fontSize: textPx(13), lineHeight: 1.5, display: "block", marginTop: 2 }}>
                      {translate("referral.rewards.friend_hint")}
                    </Text>
                  </div>
                  <Form.Item name="has_friend_reward" valuePropName="checked" noStyle>
                    <Switch aria-label={translate("referral.rewards.friend")} />
                  </Form.Item>
                </Flex>
                {hasFriendReward && rewardCoreFields("friend_reward")}
              </div>
            </SettingsSection>

            <SettingsSectionsFooter>
              {dirty && (
                <span
                  aria-label="unsaved"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: colors.amber,
                    display: "inline-block",
                  }}
                />
              )}
              <Button
                type="primary"
                disabled={!dirty || settingsRestricted}
                loading={updateConfig.isPending}
                onClick={onSave}
              >
                {translate("button.save_changes")}
              </Button>
            </SettingsSectionsFooter>

            {pendingCard}
          </SettingsSections>
        </Form>
      </ReadOnlyWhenRestricted>
    </SettingsPage>
  );
};

export default ReferralSettingsCard;
